// src/modules/units/tyres/views/retread-batch/retreadbatchreceive.view.js
//
// Pure view for receiving tyres back against a batch. Data in, HTML out; no fetch,
// no DOM. The orchestrator owns state: it renders one row per OUTSTANDING line,
// tracks each row's chosen outcome, swaps the trailing input, validates and submits.
//
// SIMPLIFIED for the current workflow: one line per tyre —
//   TYRE ID · [ Retreaded | Rejected ] · <Cost excl VAT | Reason>
// Retreaded is the default (highlighted); switching to Rejected turns the trailing
// Cost input into a required Reason input. No per-tyre notes and no new-tread capture.
//
// WRITTEN OFF is intentionally NOT offered as a button for now (management is
// expected to want it back), but it is only a UI omission: the RETREAD_OUTCOME enum,
// its reason requirement (OUTCOME_NEEDS_REASON), the vocab and the whole receive
// payload still support it, so re-adding it is a one-line change to OUTCOME_CHOICES.

import { esc } from '../../shared/tyre-format.js';
import { RETREAD_OUTCOME, OUTCOME_NEEDS_REASON } from '../../shared/retread-batch-core.js';
import { outcomeMeta } from '../../shared/retread-batch-vocab.js';

export const DEFAULT_OUTCOME = RETREAD_OUTCOME.RETREADED;

// UI-only pseudo-outcome: this tyre isn't in this delivery, so it's left outstanding
// and omitted from the payload (a partial receive -> the batch stays
// PARTIALLY_RETURNED). It is deliberately NOT a RETREAD_OUTCOME — the orchestrator
// treats a row carrying it as "skip". Namespaced so it can't collide with an enum value.
export const NOT_RECEIVED = 'NOT_RECEIVED::SKIP';

// The real outcomes offered as buttons, in order. Written off is deliberately absent
// from THIS list (not from the enum) — put RETREAD_OUTCOME.WRITTEN_OFF back here to
// show it. "Not received" is appended after these by outcomeButtons.
const OUTCOME_CHOICES = [RETREAD_OUTCOME.RETREADED, RETREAD_OUTCOME.REJECTED];

function needsReason(outcome) {
  return OUTCOME_NEEDS_REASON.includes(outcome);
}

function choiceKind(outcome) {
  if (outcome === RETREAD_OUTCOME.REJECTED) return 'reject';
  if (outcome === RETREAD_OUTCOME.WRITTEN_OFF) return 'writeoff';
  if (outcome === NOT_RECEIVED) return 'skip';
  return 'retread';
}

function choiceButton(value, label, on) {
  return `<button type="button" class="tm-rb-recv-choice-btn tm-rb-recv-choice-${choiceKind(value)}${on ? ' is-selected' : ''}"`
    + ` data-receive-outcome-btn data-outcome="${esc(value)}" aria-pressed="${on ? 'true' : 'false'}">${esc(label)}</button>`;
}

// The segmented outcome control: the real outcomes, then "Not received". The selected
// choice is highlighted; the orchestrator toggles it on click and reads it back from
// data-outcome on the row.
function outcomeButtons(selected = DEFAULT_OUTCOME) {
  const btns = OUTCOME_CHOICES.map((o) => choiceButton(o, outcomeMeta(o).label, o === selected));
  btns.push(choiceButton(NOT_RECEIVED, 'Not received', selected === NOT_RECEIVED));
  return btns.join('');
}

// The single trailing slot for a row, decided by its outcome: nothing to enter for a
// tyre that isn't here (a muted note); a required Reason for a rejection; else an
// optional Cost (excl VAT). The orchestrator re-renders this slot on outcome change.
export function receiveInputHTML(outcome = DEFAULT_OUTCOME) {
  if (outcome === NOT_RECEIVED) {
    return `<span class="tm-rb-recv-skip-note">Left on the batch — receive when it arrives.</span>`;
  }
  if (needsReason(outcome)) {
    return `<input type="text" class="tm-input tm-rb-recv-reason" data-receive-field="rejectReason"`
      + ` placeholder="Reason (required)" aria-label="Rejection reason" />`;
  }
  return `<span class="tm-rb-recv-cost">`
    + `<span class="tm-rb-recv-cost-prefix">R</span>`
    + `<input type="number" step="0.01" min="0" class="tm-input" data-receive-field="cost"`
    + ` placeholder="Cost excl VAT" aria-label="Retread cost excluding VAT" />`
    + `</span>`;
}

/** One outstanding tyre on a single line: id · [Retreaded｜Rejected] · input.
 *  `outcome` preselects (defaults to Retreaded); `errorText` flags a bad row. */
export function receiveRowHTML(line = {}, { outcome = DEFAULT_OUTCOME, errorText = '' } = {}) {
  const id = line.externalTyreId ?? '';
  const err = errorText ? `<div class="tm-rb-recv-err" data-receive-err>${esc(errorText)}</div>` : '';
  return `
    <div class="tm-rb-recv-row${errorText ? ' tm-rb-recv-bad' : ''}" data-receive-row data-tyre-id="${esc(id)}" data-outcome="${esc(outcome)}">
      <div class="tm-rb-recv-line">
        <div class="tm-rb-recv-tyre"><b>${esc(id)}</b></div>
        <div class="tm-rb-recv-choice" role="group" aria-label="Outcome for tyre ${esc(id)}">
          ${outcomeButtons(outcome)}
        </div>
        <div class="tm-rb-recv-input" data-receive-input>${receiveInputHTML(outcome)}</div>
      </div>
      ${err}
    </div>`;
}

export function receiveStateRowHTML(text) {
  return `<div class="tm-rb-recv-state">${esc(text)}</div>`;
}

/** The receive shell. `lines` are the outstanding (OUT) lines. */
export function receiveHTML({ reference = '', lines = [] } = {}) {
  const ref = reference ? esc(reference) : '';
  const rows = Array.isArray(lines) && lines.length
    ? lines.map((l) => receiveRowHTML(l)).join('')
    : receiveStateRowHTML('Nothing outstanding to receive on this batch.');
  const hasLines = Array.isArray(lines) && lines.length > 0;

  return `
    <div class="tm-rb-receive tm-form">
      <div class="tm-form-head">
        <div>
          <h2 class="tm-form-title">Receive tyres${ref ? ` — ${ref}` : ''}</h2>
          <p class="tm-form-sub">Each tyre is Retreaded by default — add the cost (excl VAT). Switch a tyre to Rejected with a reason, or Not received to leave it for a later delivery.</p>
        </div>
      </div>

      <div data-receive-product></div>

      <div class="tm-form-grid tm-rb-recv-batchfields">
        <div class="tm-form-row">
          <label class="tm-form-label" for="tm-rb-recv-returned">Return date</label>
          <input id="tm-rb-recv-returned" type="date" class="tm-input" data-receive-returned />
          <p class="tm-field-help">Optional — defaults to today.</p>
        </div>
        <div class="tm-form-row tm-form-row-wide">
          <label class="tm-form-label" for="tm-rb-recv-note">Delivery note reference</label>
          <input id="tm-rb-recv-note" type="text" class="tm-input" data-receive-note placeholder="Optional — the supplier's delivery note." />
        </div>
      </div>

      <div class="tm-form-msg" data-receive-msg hidden></div>

      <div class="tm-rb-recv-list" data-receive-rows>${rows}</div>

      <div class="tm-form-actions">
        <button type="button" class="tm-btn-secondary" data-receive-cancel>Cancel</button>
        <button type="button" class="tm-btn-primary" data-receive-submit${hasLines ? '' : ' disabled'}>Receive tyres</button>
      </div>
    </div>`;
}
